import { useState } from 'react'
import { Link } from 'react-router-dom'
import { doc, updateDoc, deleteDoc, serverTimestamp } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { Pencil, CheckCircle, Archive, RotateCcw, Trash2, XCircle } from 'lucide-react'
import type { Listing } from '@/types/listing'

interface ListingStatusControlsProps {
  listing: Listing
  onStatusChange?: (newStatus: string) => void
}

export function ListingStatusControls({ listing, onStatusChange }: ListingStatusControlsProps) {
  const { showToast } = useUIStore()
  const [loading, setLoading] = useState<string | null>(null)

  const updateStatus = async (status: string, label: string) => {
    setLoading(status)
    try {
      await updateDoc(doc(db, 'listings', listing.id), {
        status,
        updatedAt: serverTimestamp(),
      })
      showToast(`Listing marked as ${label}`)
      onStatusChange?.(status)
    } catch {
      showToast(`Failed to update listing`, 'error')
    } finally {
      setLoading(null)
    }
  }

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this listing? This cannot be undone.')) return
    setLoading('delete')
    try {
      await deleteDoc(doc(db, 'listings', listing.id))
      showToast('Listing deleted')
      onStatusChange?.('deleted')
    } catch {
      showToast('Failed to delete listing', 'error')
    } finally {
      setLoading(null)
    }
  }

  return (
    <div className="card p-4">
      <h3 className="font-semibold mb-3 text-sm text-slate-600 uppercase tracking-wide">Manage Listing</h3>
      <div className="flex flex-wrap gap-2">
        <Link to={`/listing/${listing.id}/edit`}>
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Pencil className="w-4 h-4" />}
          >
            Edit
          </Button>
        </Link>
        {listing.status === 'active' && (
          <>
            {listing.listingType === 'fundraiser' ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatus('closed', 'closed')}
                loading={loading === 'closed'}
                leftIcon={<XCircle className="w-4 h-4" />}
              >
                Close Fundraiser
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => updateStatus('sold', 'sold')}
                loading={loading === 'sold'}
                leftIcon={<CheckCircle className="w-4 h-4" />}
              >
                Mark Sold
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => updateStatus('draft', 'draft')}
              loading={loading === 'draft'}
              leftIcon={<Archive className="w-4 h-4" />}
            >
              Move to Draft
            </Button>
          </>
        )}
        {(listing.status === 'sold' || listing.status === 'closed' || listing.status === 'draft' || listing.status === 'expired') && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => updateStatus('active', 'active')}
            loading={loading === 'active'}
            leftIcon={<RotateCcw className="w-4 h-4" />}
          >
            Relist
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={handleDelete}
          loading={loading === 'delete'}
          leftIcon={<Trash2 className="w-4 h-4" />}
          className="!border-red-300 !text-red-600 hover:!bg-red-50"
        >
          Delete
        </Button>
      </div>
    </div>
  )
}
